/**
 * Permission-aware UI (HANDOFF §0.3, 11 §3): the UI reads `GET /me` → `currentTenant.permissions` and hides or
 * disables; the server enforces. Product-scoped permissions also need the product in `productScope`.
 * Fail closed: nothing is allowed while `/me` is loading or failed.
 */
import type { ReactNode } from "react";
import type { CurrentTenant } from "@brevalt/api-client/customer/model";
import { useEntitlements } from "./entitlements";

/** A rule: one permission, or any of several. */
export type PermissionRule = string | { anyOf: readonly string[] };

export function allows(
  tenant: Pick<CurrentTenant, "permissions" | "productScope"> | null | undefined,
  rule: PermissionRule | undefined,
  productId?: string,
): boolean {
  if (rule === undefined) return true;
  if (!tenant) return false;
  const held = tenant.permissions;
  const ok = typeof rule === "string" ? held.includes(rule) : rule.anyOf.some((p) => held.includes(p));
  if (!ok) return false;
  if (productId === undefined) return true;
  return tenant.productScope.all || (tenant.productScope.productIds ?? []).includes(productId);
}

/** `can("case.submit")`, `can("case.submit", productId)`, `can({ anyOf: [...] })`. */
export function useCan(): (rule: PermissionRule | undefined, productId?: string) => boolean {
  const { tenant } = useEntitlements();
  return (rule, productId) => allows(tenant, rule, productId);
}

/**
 * Renders `children` only with the permission (hide), or always with the outcome (disable):
 * `<Can permission="case.submit">{(allowed) => <Button disabled={!allowed}>…</Button>}</Can>`.
 */
export function Can({
  permission,
  productId,
  fallback = null,
  children,
}: {
  permission: PermissionRule;
  productId?: string;
  fallback?: ReactNode;
  children: ReactNode | ((allowed: boolean) => ReactNode);
}) {
  const can = useCan();
  const allowed = can(permission, productId);
  if (typeof children === "function") return <>{children(allowed)}</>;
  return <>{allowed ? children : fallback}</>;
}
